import React from 'react';
import { Link } from 'react-router-dom';
import './Docs.css';

// Quickstart uses the same calm layout as Docs Lite; no presence logic is loaded.
export default function Quickstart() {
  return (
    <div className="docs-lite">
      <header className="docs-lite__header">
        <h1>JG Presence SDK — 5-Minute Quickstart</h1>
        <p className="docs-lite__lede">A minimal path from zero to live presence.</p>
      </header>

      <main className="docs-lite__content">
        <div className="docs-lite__spacer" />
        <section>
          <h2>What this does</h2>
          <p>
            This quickstart connects your app to the same live presence system powering the Presence Engine Demo.
          </p>
          <p>In a few minutes, you’ll have:</p>
          <ul>
            <li>live guest presence</li>
            <li>real-time lifecycle updates</li>
            <li>sessions visible instantly in the Presence Engine Demo</li>
          </ul>
          <p>No authentication is required to start.</p>
        </section>

        <div className="docs-lite__spacer" />
        <section>
          <h2>Install</h2>
          <p>Install the SDK using the package provided for early access.</p>
          <pre>
            <code>{`npm install @joegalaxy/presence`}</code>
          </pre>
          <p>That’s it. No peer dependencies. No setup scripts.</p>
        </section>

        <div className="docs-lite__spacer" />
        <section>
          <h2>Initialize presence</h2>
          <p>Import and initialize the SDK once, near your app startup.</p>
          <pre>
            <code>{`import { createPresenceClient } from '@joegalaxy/presence';

const presence = createPresenceClient({
  apiKey: 'YOUR_API_KEY',
});`}</code>
          </pre>
          <p>At this point, nothing is sent yet.</p>
          <p>Presence activates when a session joins.</p>
        </section>

        <div className="docs-lite__spacer" />
        <section>
          <h2>Join a presence space</h2>
          <p>Join a presence space (room, page, or context of your choice).</p>
          <pre>
            <code>{`presence.join({
  space: 'example-space',
});`}</code>
          </pre>
          <p>This single call:</p>
          <ul>
            <li>creates a live session</li>
            <li>begins lifecycle tracking</li>
            <li>emits presence updates in real time</li>
          </ul>
          <p>Open another browser or device and repeat this step to see multiple sessions.</p>
        </section>

        <div className="docs-lite__spacer" />
        <section>
          <h2>Observe live updates</h2>
          <p>You can subscribe to presence updates if you want to react in your UI.</p>
          <pre>
            <code>{`presence.on('update', (state) => {
  console.log(state);
});`}</code>
          </pre>
          <p>The state reflects:</p>
          <ul>
            <li>active sessions</li>
            <li>lifecycle changes (active, idle, away)</li>
            <li>joins and leaves</li>
          </ul>
          <p>Rendering visuals is optional.</p>
          <p>Presence works even if you do nothing with the data.</p>
        </section>

        <div className="docs-lite__spacer" />
        <section>
          <h2>Verify it’s live</h2>
          <p>Open the Presence Engine Demo:</p>
          <p>
            <a
              href="https://www.joegalaxy.net/presence-demo"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://www.joegalaxy.net/presence-demo
            </a>
          </p>
          <p>Your session will appear there instantly.</p>
          <p>Nothing is simulated.</p>
          <p>This is the same live presence system.</p>
        </section>

        <div className="docs-lite__spacer" />
        <section>
          <h2>Authentication (optional, later)</h2>
          <p>Presence does not require identity.</p>
          <p>Guest sessions:</p>
          <ul>
            <li>connect immediately</li>
            <li>participate fully</li>
            <li>can be promoted later</li>
          </ul>
          <p>When a user authenticates, their presence is promoted, not replaced.</p>
          <p>The same session continues and identity is layered on top.</p>
          <p>You do not need this for the quickstart.</p>
        </section>

        <div className="docs-lite__spacer" />
        <section>
          <h2>That’s it</h2>
          <p>At this point:</p>
          <ul>
            <li>presence is live</li>
            <li>sessions are tracked</li>
            <li>lifecycle is active</li>
            <li>the system is observable in real time</li>
          </ul>
          <p>Everything else—visuals, sound, clustering, promotion—is built on top of this signal.</p>
        </section>

        <div className="docs-lite__spacer" />
        <section>
          <h2>Next steps</h2>
          <p>Explore the live demo</p>
          <p>
            <a
              href="https://www.joegalaxy.net/presence-demo"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://www.joegalaxy.net/presence-demo
            </a>
          </p>
          <p>Read how presence is modeled</p>
          <p>
            <Link to="/docs">/docs</Link>
          </p>
          <p>Request early access for authenticated SDK features</p>
          <p>
            <a
              href="https://presence-sdk-landing.joegalaxy.net"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://presence-sdk-landing.joegalaxy.net
            </a>
          </p>
        </section>

        <div className="docs-lite__spacer" />
        <section>
          <h2>Philosophy</h2>
          <p>JG Presence Engine does not impose a presence model.</p>
          <p>It exposes presence as a signal you can shape.</p>
        </section>

        {/* Cross-navigation strip for docs cohesion */}
        <div className="docs-lite__xnav">
          <a href="https://www.joegalaxy.net/presence-demo">← Back to live presence</a>
          <span style={{ padding: '0 8px' }}>|</span>
          <Link to="/docs">Docs</Link> · <Link to="/docs/quickstart">5-Minute Quickstart</Link> ·{' '}
          <Link to="/docs/pricing">Pricing model</Link>
        </div>
      </main>
    </div>
  );
}
